import { IHttpService } from "../HttpService";
import { parseBooleanValue } from "../..";
import { IQualifier } from "../../model/formconfiguration/Qualifier";
import QualifierListMapper from "./Mappers/QualifierListMappers";
import { IFormConfigQualifierAttribute } from "./FormConfigQualifierAttribute";

export interface IQualiferService {
  getQualifiers(): Promise<IQualifier[]>;
  createQualifier(qualifier: IQualifier, formTypeCode: string): Promise<any>;
  updateQualifier(qualifierCode: string, qualifier: IQualifier): Promise<any>;
  invalidateQualifier(id: number): Promise<any>;
  createFormConfigQualifierAttribute(
    qualifierCode: string,
    formConfigQualifierAttribute: IFormConfigQualifierAttribute
  ): Promise<string>;
  updateFormConfigQualifierAttribute(
    qualifierCode: string,
    attributeCode: string,
    formConfigQualifierAttribute: IFormConfigQualifierAttribute
  ): Promise<string>;
}

const QualifierService = (httpService): IQualiferService => {
  const _http: IHttpService = httpService;
  const _baseUrl: string = "/api/qualifiers";
  const _attributesPath: string = "/attributes";

  const QualifierParamsMapper = (qualifier: IQualifier) => {
    return {
      ...qualifier,
      valid: parseBooleanValue(qualifier.valid),
      visiblePublic: parseBooleanValue(qualifier.visiblePublic)
    };
  };

  const FormConfigQualifierAttributeParamsMapper = (
    formConfigQualifierAttribute: IFormConfigQualifierAttribute
  ) => {
    return {
      ...formConfigQualifierAttribute,
      required: parseBooleanValue(formConfigQualifierAttribute.required),
      labelVisible: parseBooleanValue(
        formConfigQualifierAttribute.labelVisible
      ),
      shortDisplay: parseBooleanValue(
        formConfigQualifierAttribute.shortDisplay
      ),
      validation: parseBooleanValue(formConfigQualifierAttribute.validation),
      visible: parseBooleanValue(formConfigQualifierAttribute.visible)
    };
  };

  return {
    async getQualifiers() {
      const path = _http.buildPath(_baseUrl);
      const response = await _http.get(path);
      const responseJSON = await _http.toJSON(response);
      return QualifierListMapper(responseJSON);
    },

    async createQualifier(qualifier: IQualifier, formTypeCode: string) {
      const path = _http.buildPath(_baseUrl);
      const response = await _http.post(path, {
        params: QualifierParamsMapper(qualifier),
        query: { formTypeCode }
      });
      return _http.toText(response);
    },

    async updateQualifier(qualifierCode: string, qualifier: IQualifier) {
      const path = _http.buildPath(_baseUrl, qualifierCode);
      const response = await _http.put(path, {
        params: QualifierParamsMapper(qualifier)
      });
      return _http.toText(response);
    },

    async invalidateQualifier(id: number) {
      const path = _http.buildPath(_baseUrl, id.toString());
      const response = await _http.remove(path);
      return _http.toText(response);
    },

    async createFormConfigQualifierAttribute(
      qualifierCode: string,
      formConfigQualifierAttribute: IFormConfigQualifierAttribute
    ) {
      const path = _http.buildPath(_baseUrl, qualifierCode, _attributesPath);
      const response = await _http.post(path, {
        params: FormConfigQualifierAttributeParamsMapper(
          formConfigQualifierAttribute
        )
      });
      return _http.toText(response);
    },

    async updateFormConfigQualifierAttribute(
      qualifierCode: string,
      attributeCode: string,
      formConfigQualifierAttribute: IFormConfigQualifierAttribute
    ) {
      const path = _http.buildPath(
        _baseUrl,
        qualifierCode,
        _attributesPath,
        attributeCode
      );
      const response = await _http.put(path, {
        params: FormConfigQualifierAttributeParamsMapper(
          formConfigQualifierAttribute
        )
      });
      return _http.toText(response);
    }
  };
};

export default QualifierService;
